/**
 * Authors Storefront Loader
 * Fetches all author metaobjects and computes category membership via products.
 */

(() => {
  const grid = document.querySelector("[data-authors-grid]");
  if (!grid) return;

  const loader = grid.querySelector("[data-authors-loading]");
  const errorMessage = grid.querySelector("[data-authors-error]");

  const setLoadingState = (isLoading) => {
    grid.setAttribute("aria-busy", isLoading ? "true" : "false");

    if (loader) loader.hidden = !isLoading;
    if (errorMessage) errorMessage.hidden = true;
  };

  const showErrorState = () => {
    grid.setAttribute("aria-busy", "false");
    if (loader) loader.hidden = true;
    if (errorMessage) errorMessage.hidden = false;
  };

  const token = grid.dataset.storefrontToken;
  const apiVersion = grid.dataset.storefrontApiVersion || "2024-10";

  if (!token) return;

  const endpoint = `${window.location.origin}/api/${apiVersion}/graphql.json`;

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  };

  const storefrontRequest = async (query, variables) => {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed (${response.status})`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      const message = payload.errors.map((e) => e.message).join("; ");
      throw new Error(message);
    }

    return payload.data;
  };

  const getAuthorSortKey = (fullName) => {
    const trimmed = String(fullName ?? "").trim();
    if (!trimmed) return { last: "", full: "" };

    const parts = trimmed.split(/\s+/g);
    const last = String(parts.at(-1) ?? "").toLowerCase();

    return { last, full: trimmed.toLowerCase() };
  };

  const fetchAllAuthors = async () => {
    const query = `
      query Authors($first: Int!, $after: String) {
        metaobjects(type: "author", first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          edges {
            node {
              id
              handle
              name: field(key: "name") { value }
              photo: field(key: "photo") {
                reference {
                  ... on MediaImage {
                    image {
                      url(transform: { maxWidth: 300 })
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const authors = [];
    let after = null;

    for (;;) {
      const data = await storefrontRequest(query, { first: 100, after });
      const connection = data?.metaobjects;
      const edges = connection?.edges ?? [];

      for (const edge of edges) {
        const node = edge?.node;
        if (!node?.id || !node?.handle) continue;

        const fullName = node?.name?.value ?? "";
        const image = node?.photo?.reference?.image;

        authors.push({
          id: node.id,
          handle: node.handle,
          name: fullName,
          image: image
            ? {
                url: image.url,
                altText: image.altText ?? "",
                width: image.width ?? 300,
                height: image.height ?? 300,
              }
            : null,
        });
      }

      if (!connection?.pageInfo?.hasNextPage) break;
      after = connection.pageInfo.endCursor;
      if (!after) break;
    }

    authors.sort((a, b) => {
      const aKey = getAuthorSortKey(a.name);
      const bKey = getAuthorSortKey(b.name);

      if (aKey.last < bKey.last) return -1;
      if (aKey.last > bKey.last) return 1;
      if (aKey.full < bKey.full) return -1;
      if (aKey.full > bKey.full) return 1;
      return 0;
    });

    return authors;
  };

  const fetchAuthorCollections = async () => {
    const query = `
      query Products($first: Int!, $after: String) {
        products(first: $first, after: $after) {
          pageInfo { hasNextPage endCursor }
          nodes {
            collections(first: 20) {
              nodes { handle }
            }
            authors: metafield(namespace: "books", key: "authors") {
              references(first: 20) {
                nodes {
                  ... on Metaobject { id }
                }
              }
            }
            editors: metafield(namespace: "books", key: "editors") {
              references(first: 20) {
                nodes {
                  ... on Metaobject { id }
                }
              }
            }
          }
        }
      }
    `;

    const authorIdToCollections = new Map();
    let after = null;

    for (;;) {
      const data = await storefrontRequest(query, { first: 100, after });
      const connection = data?.products;
      const products = connection?.nodes ?? [];

      for (const product of products) {
        const handles = (product?.collections?.nodes ?? [])
          .map((c) => c?.handle)
          .filter(Boolean)
          .filter((h) => h !== "all");

        if (!handles.length) continue;

        const authorIds = new Set();

        const authorNodes = product?.authors?.references?.nodes ?? [];
        for (const node of authorNodes) {
          if (node?.id) authorIds.add(node.id);
        }

        const editorNodes = product?.editors?.references?.nodes ?? [];
        for (const node of editorNodes) {
          if (node?.id) authorIds.add(node.id);
        }

        if (!authorIds.size) continue;

        for (const authorId of authorIds) {
          const set = authorIdToCollections.get(authorId) ?? new Set();
          for (const handle of handles) set.add(handle);
          authorIdToCollections.set(authorId, set);
        }
      }

      if (!connection?.pageInfo?.hasNextPage) break;
      after = connection.pageInfo.endCursor;
      if (!after) break;
    }

    return authorIdToCollections;
  };

  const renderAuthorCard = (author, collectionsCsv) => {
    const safeName = escapeHtml(author.name);
    const safeHandle = escapeHtml(author.handle);
    const safeCollections = escapeHtml(collectionsCsv);

    const imageHtml = author.image
      ? `
        <div class="authors-page__author-image">
          <img
            src="${escapeHtml(author.image.url)}"
            alt=""
            width="300"
            height="300"
            loading="lazy"
          >
        </div>
      `
      : `
        <div class="authors-page__author-image authors-page__author-image--placeholder">
          <div class="authors-page__author-placeholder" aria-hidden="true">
            ${escapeHtml(
              String(author.name ?? "")
                .trim()
                .slice(0, 2)
                .toUpperCase(),
            )}
          </div>
        </div>
      `;

    return `
      <div class="authors-page__author-card" data-collections="${safeCollections}">
        <a
          href="/pages/author/${safeHandle}"
          class="authors-page__author-link"
          aria-label="View ${safeName}'s profile"
        >
          ${imageHtml}
          <h3 class="authors-page__author-name">${safeName}</h3>
        </a>
      </div>
    `;
  };

  const renderEmptyState = () => {
    return `
      <div class="authors-page__empty">
        <h2>No authors found</h2>
        <p>Check back soon for author profiles.</p>
      </div>
    `;
  };

  const main = async () => {
    setLoadingState(true);

    try {
      const authorIdToCollections = await fetchAuthorCollections();
      const authors = await fetchAllAuthors();

      if (!authors.length) {
        grid.innerHTML = renderEmptyState();
        return;
      }

      const cards = authors
        .map((author) => {
          const collections = authorIdToCollections.get(author.id);
          const collectionsCsv = collections
            ? Array.from(collections).sort().join(",")
            : "";

          return renderAuthorCard(author, collectionsCsv);
        })
        .join("");

      grid.innerHTML = cards;
      grid.setAttribute("aria-busy", "false");
    } catch (error) {
      // If Storefront API fails, avoid breaking the page entirely.
      // The Liquid-rendered fallback (50 cap) will still show if present.
      console.error(error);
      showErrorState();
    }
  };

  main();
})();
